// React
import { RefObject } from "react";

// Interfaces (shared/external)
import { I_ApprovalStatusTypesWithRules } from "src/components/approvals/interfaces";

// Services
import approvalService from "src/components/approvals/ApprovalService";

// Constants
import { VehicleIncidentsTableKey } from "./constants/VehicleIncidentsTableItems";
import vehicleSafetyEventsReferenceBoilerplate from "./constants/VehicleSafetyEventsReferenceBoilerplate";

// Data fetching functions (CRUD)
import getVehicleSafetyEventsLazy from "./crud/GetVehicleSafetyEventsLazy";
import getVehicleSafetyEventsReference from "./crud/GetVehicleSafetyEventsReference";
import getCountsForSwitchView from "./crud/GetCountsForSwitchView";
import getSafetyEventsWithApprovalHistory from "./crud/GetSafetyEventsWithApprovalHistory";

//libraries
import dayjs from "dayjs";

//enums
import { LoadState } from "./enums";

// Local interfaces and types
import {
  I_SafetyEvent,
  I_SafetyEventWithApprovalHistory,
  I_VehicleIncidentsFilters,
} from "./interfaces";
import {
  T_SetEvents,
  T_SetLoader,
  T_SetIncidentAction,
  T_SetLazyLoading,
  T_SetSort,
  T_SetStatusCounts,
  T_SetChecked,
  T_SetOpenVideo,
} from "./types";

// Constants
const INITIAL_SKIP = 0;
const INITIAL_TAKE = 150;
const VIEW_ALL_OPEN = -1;
const VIEW_STARTED = -999;

// Vehicle Incident Service (shared singleton object)
const VIS = {
  // Current user ID and admin flag (injected during init)
  loggedInEmployeeId: -1,
  isAdmin: false,

  // Pagination
  skip: INITIAL_SKIP,
  take: INITIAL_TAKE,
  threshold: 400, // scroll threshold in px for triggering lazy load
  busy: false, // flag to prevent overlapping requests

  //Search filters
  filters: {
    stateCode: undefined as string | undefined,
    city: undefined as string | undefined,
  } as I_VehicleIncidentsFilters,

  // Scroll reference and sorting state
  scrollRef: null as unknown as RefObject<HTMLDivElement>,
  sortBy: "Name" as VehicleIncidentsTableKey,
  sortDirection: "asc" as "asc" | "desc",

  // Cached reference data and lookup maps
  reference: vehicleSafetyEventsReferenceBoilerplate,
  eventMap: new Map<number, I_SafetyEvent>(),
  approvalStatusTypesMap: new Map<string, I_ApprovalStatusTypesWithRules>(),

  // Current view tab (used to filter by approval status)
  currentView: VIEW_ALL_OPEN,

  // These setter functions are injected by the VehicleIncidents component
  // ——— injected setters (start undefined) ———
  setLoader: undefined as unknown as T_SetLoader,
  setEvents: undefined as unknown as T_SetEvents,
  setIncidentAction: undefined as unknown as T_SetIncidentAction,
  setLazyLoading: undefined as unknown as T_SetLazyLoading,
  setSort: undefined as unknown as T_SetSort,
  setStatusCounts: undefined as unknown as T_SetStatusCounts,
  setChecked: undefined as unknown as T_SetChecked,
  setOpenVideo: undefined as unknown as T_SetOpenVideo,

  /** Throws if any required setter hasn’t been wired up by VehicleIncidents */
  assertVehicleIncidentsSettersInitialized(): void {
    const missing: string[] = [];
    if (!VIS.setLoader) missing.push("setLoader");
    if (!VIS.setEvents) missing.push("setEvents");
    if (!VIS.setIncidentAction) missing.push("setIncidentAction");
    if (!VIS.setStatusCounts) missing.push("setStatusCounts");
    if (!VIS.setOpenVideo) missing.push("setOpenVideo");
    if (missing.length) {
      throw new Error(
        `VIS initialisation error – missing setter(s) from VehicleIncidents.tsx: ${missing.join(
          ", "
        )}`
      );
    }
  },
  assertVehicleIncidentsTableSettersInitialized(): void {
    const missing: string[] = [];
    if (!VIS.setLazyLoading) missing.push("setLazyLoading");
    if (missing.length) {
      throw new Error(
        `VIS initialisation error – missing setter(s) from VehicleIncidentsTable.tsx : ${missing.join(
          ", "
        )}`
      );
    }
  },
  assertVehicleIncidentsTableHeaderSettersInitialized(): void {
    const missing: string[] = [];
    if (!VIS.setSort) missing.push("setSort");
    if (missing.length) {
      throw new Error(
        `VIS initialisation error – missing setter(s) from VehicleIncidentsTableHeader.tsx : ${missing.join(
          ", "
        )}`
      );
    }
  },
  assertVehicleIncidentsSwitchViewInitialized(): void {
    const missing: string[] = [];
    if (!VIS.setChecked) missing.push("setChecked");
    if (missing.length) {
      throw new Error(
        `VIS initialisation error – missing setter(s) from VehicleIncidentsSwitchView.tsx : ${missing.join(
          ", "
        )}`
      );
    }
  },
  // Fetch the next page of events and update state
  getNextEvents: async (): Promise<void> => {
    const { sortBy, sortDirection, setEvents, setLazyLoading } = VIS;
    if (VIS.busy) return;

    VIS.skip += VIS.take;
    setLazyLoading(true);
    VIS.busy = true;

    try {
      const data = await getVehicleSafetyEventsLazy(
        VIS.skip,
        VIS.take,
        sortBy,
        sortDirection,
        VIS.filters
      );

      if (data.length === 0) return;

      data.forEach((event) => VIS.eventMap.set(event.ObjectID, event));
      setEvents((prev) => [...prev, ...data]);
    } finally {
      VIS.busy = false;
      setLazyLoading(false);
    }
  },

  // Attach scroll listener to element that triggers lazy loading near bottom
  handleScroll: (scrollRef: React.RefObject<HTMLDivElement>) => {
    return () => {
      const el = scrollRef.current;
      if (!el || VIS.busy) return;

      const nearBottom =
        el.scrollTop + el.clientHeight >= el.scrollHeight - VIS.threshold;
      if (nearBottom) {
        VIS.getNextEvents();
      }
    };
  },
  // Sort events by column and direction
  handleSort: async (
    sortBy: VehicleIncidentsTableKey,
    sortDirection: "asc" | "desc"
  ): Promise<void> => {
    VIS.sortDirection = sortDirection
    VIS.sortBy = sortBy
    VIS.resetSkipAndTake();
    VIS.setSort({ activeKey: sortBy, sortDirection });

    const sortedEvents = await getVehicleSafetyEventsLazy(
      VIS.skip,
      VIS.take,
      sortBy,
      sortDirection,
      VIS.filters
    );
    VIS.scrollToTop();
    VIS.setEvents(sortedEvents);
  },
  // Resets pagination indexes
  resetSkipAndTake: (): void => {
    VIS.skip = INITIAL_SKIP;
    VIS.take = INITIAL_TAKE;
  },
  // Scroll to top of event list container
  scrollToTop: (): void => {
    const el = VIS.scrollRef.current;
    if (el) el.scrollTo({ top: 0, behavior: "smooth" });
  },
  buildEventsMap: (events: I_SafetyEvent[]): void => {
    VIS.eventMap.clear();
    events.forEach((event) => VIS.eventMap.set(event.ObjectID, event));
  },
  buildApprovalStatusTypesMap: (
    approvalStatusTypes: I_ApprovalStatusTypesWithRules[]
  ) => {
    VIS.approvalStatusTypesMap.clear();
    approvalStatusTypes.forEach((ast) => {
      VIS.approvalStatusTypesMap.set(`${ast.ApprovalStatusTypeId}`, ast);
    });
  },
  //format time sol using dayjs and the format from approval service
  formatTimeColumn: (s: string): string => {
    return dayjs(s).format(approvalService.timeFormat)
  },
  retrieveEventFromMap: (
    ObjectID: number
  ): I_SafetyEvent | I_SafetyEventWithApprovalHistory | null =>
    VIS.eventMap.get(ObjectID) ?? null,

  retrieveStatusFromMap: (
    approvalStatusTypeId: number
  ): I_ApprovalStatusTypesWithRules | null =>
    VIS.approvalStatusTypesMap.get(`${approvalStatusTypeId}`) ?? null,

  // Build reviewer options for dropdowns/forms
  getAllowedReviewersForForm: (): { name: string; id: number }[] => {
    return VIS.reference.AllowedReviewers.map((employeeId) => {
      const emp = approvalService.retrieveEmployeeFromMap(employeeId);
      if (!emp) return null;
      const fullName = [emp.FirstName, emp.MiddleInit, emp.LastName]
        .filter(Boolean)
        .join(" ");
      return { name: fullName, id: emp.EmployeeID };
    }).filter((opt): opt is { name: string; id: number } => opt !== null);
  },
  // Type guard for checking ApprovalHistory presence
  isWithApprovalHistory: (
    event: I_SafetyEvent | I_SafetyEventWithApprovalHistory | null
  ): event is I_SafetyEventWithApprovalHistory => {
    return event !== null && "ApprovalHistory" in event;
  },
  isLastApprover: (
    event: I_SafetyEvent | I_SafetyEventWithApprovalHistory
  ): boolean => {
    if (!VIS.isWithApprovalHistory(event)) return false;

    const last = [...event.ApprovalHistory]
      .sort((a, b) => b.StepOrder - a.StepOrder)
      .find((h) => h.ApproverUserId != null);

    return last?.ApproverUserId === VIS.loggedInEmployeeId;
  },
  // ...Refresh data when changing tabs/views by approval status
  refresh: async (approvalStatusId: number, loader?: number) => {
    VIS.currentView = approvalStatusId;

    const {
      loggedInEmployeeId: userId,
      sortBy,
      sortDirection,
      setLoader,
      setIncidentAction,
      resetSkipAndTake,
      setEvents,
      setStatusCounts,
      buildEventsMap,
    } = VIS;

    if (loader) {
      setLoader(loader);
    }

    resetSkipAndTake();

    const statusCounts = await getCountsForSwitchView(VIS.filters);

    if (approvalStatusId === VIEW_STARTED) {
      // Events initiated by the current user
      const startedByMe = await getSafetyEventsWithApprovalHistory(
        userId,
        null,
        true
      );
      setEvents(startedByMe);
      buildEventsMap(startedByMe);
    } else if (approvalStatusId < 1) {
      // All open/pending events — use VIS.skip/take after reset, not stale destructured values
      const events = await getVehicleSafetyEventsLazy(
        VIS.skip,
        VIS.take,
        sortBy,
        sortDirection,
        VIS.filters
      );
      setEvents(events);
      buildEventsMap(events);
    } else {
      // Specific approvalStatusId
      const events = await getSafetyEventsWithApprovalHistory(
        userId,
        approvalStatusId,
        false
      );
      setEvents(events);
      buildEventsMap(events);
    }

    setLoader(LoadState.Idle);
    setIncidentAction({ action: -1, id: -1 });
    setStatusCounts(statusCounts);
  },
  // Finds the current user's approval step ID from an event’s audit history
  findStepId: (
    event: I_SafetyEvent | I_SafetyEventWithApprovalHistory
  ): number => {
    if ("ApprovalHistory" in event && Array.isArray(event.ApprovalHistory)) {
      const step = event.ApprovalHistory.find(
        (h) => h.ApproverUserId === VIS.loggedInEmployeeId
      );
      return step?.ApprovalStepId ?? -1;
    }
    return -1;
  },
  isAuthorizedEventEditor: (): boolean => {
    return (
      VIS.isAdmin ||
      VIS.reference.AllowedReviewers.includes(VIS.loggedInEmployeeId)
    );
  },

  updateEventLabels: (objectId: number, newLabelString: string): void => {
    const existing = VIS.eventMap.get(objectId);
    if (!existing) return;
    const updated = { ...existing, Label: newLabelString };
    VIS.eventMap.set(objectId, updated);
    VIS.setEvents((prev) =>
      prev.map((e) => (e.ObjectID === objectId ? updated : e))
    );
  },

  // The First/Last Name columns resolve through approvalService.retrieveEmployeeFromMap(EmployeeID),
  // so patching EmployeeID alone re-renders both without a refetch.
  updateEventEmployee: (objectId: number, employeeId: number): void => {
    const existing = VIS.eventMap.get(objectId);
    if (!existing) return;
    const updated = { ...existing, EmployeeID: employeeId };
    VIS.eventMap.set(objectId, updated);
    VIS.setEvents((prev) =>
      prev.map((e) => (e.ObjectID === objectId ? updated : e))
    );
  },

  // Initializes all reference data, events, and maps
  init: async (): Promise<void> => {
    const {
      sortBy,
      sortDirection,
      setEvents,
      setLoader,
      setStatusCounts,
      buildEventsMap,
      buildApprovalStatusTypesMap,
    } = VIS;

    VIS.reference = await getVehicleSafetyEventsReference();
    const firstBatch = await getVehicleSafetyEventsLazy(
      VIS.skip,
      VIS.take,
      sortBy,
      sortDirection,
      VIS.filters
    );
    const statusCounts = await getCountsForSwitchView(VIS.filters);

    buildEventsMap(firstBatch);
    buildApprovalStatusTypesMap(VIS.reference.ApprovalStatusTypesWithRules);

    setLoader(LoadState.Idle);
    setEvents(firstBatch);
    setStatusCounts(statusCounts);
  },
};

export default VIS;
